/**
 * 企业账号权限（法务）接口层
 *
 * 对接后端 payadmin/enterpriseaccount 模块，本层负责后端契约与页面模型之间的字段映射：
 * - 账号：enterpriseAccountId/ssoUserId/displayName ↔ accountId/ssoUserId/accountName
 *   （enterpriseAccountId 为后端 long，入参一律按字符串下发，避免大数在 JS 侧丢精度）
 * - 产品：dbtId/appName/pkg/customStoreName ↔ productId/productName/packageName/customName
 * - 可查询时间：queryTimeRanges[{start,end}] ↔ queryTimeMode + timeRanges[{startDate,endDate}]
 *   （后端无 queryTimeMode，空数组即「不限」）
 * - 写接口：后端只有 saveConfig 一个写入口，自定义商品名与可查询时间按 partial 标记分别下发
 *
 * 需求：https://ewe8txd9jt.feishu.cn/wiki/D4pFw48uXiohhIkR3gAc88eHnvb
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import request from './request'
import type {
  LawyerAccount,
  LawyerProduct,
  LawyerResponse,
  PageResult,
  QueryTimeMode,
  SimpleUserInfo,
  TimeRange,
} from '@/views/lawyerBoard/types'

const serveName = process.env.VUE_APP_SERVENAME

/** 后端账号行 */
interface RawAccount {
  enterpriseAccountId: number | string
  ssoUserId: number | string
  displayName?: string
  userName?: string
  recovered?: boolean
}

/** 后端产品行 */
interface RawProduct {
  dbtId: string
  appName?: string
  pkg?: string
  storeName?: string
  customStoreName?: string | null
  queryTimeRanges?: Array<{ start: string; end: string }> | null
}

/** 后端下拉用户行 */
interface RawSimpleUser {
  id: number | string
  name?: string
  userName?: string
}

/**
 * 统一响应外壳：后端 code 可能是数字 0，而项目内 OK 常量为字符串 '0'，
 * 这里统一转成字符串，页面侧 `res.code === OK` 的判断保持不变。
 */
const wrap = <T>(res: any, mapData: (data: any) => T): LawyerResponse<T> => ({
  code: String(res?.code ?? ''),
  msg: res?.msg || '',
  data: mapData(res?.data),
})

const toAccount = (raw: RawAccount): LawyerAccount => ({
  accountId: String(raw.enterpriseAccountId),
  ssoUserId: String(raw.ssoUserId),
  accountName: raw.displayName || raw.userName || String(raw.ssoUserId),
  userName: raw.userName || '',
})

const toProduct = (raw: RawProduct): LawyerProduct => {
  const ranges = (raw.queryTimeRanges || []).map((item) => ({
    startDate: item.start,
    endDate: item.end,
  }))
  return {
    productId: raw.dbtId,
    dbtid: raw.dbtId,
    productName: raw.appName || '',
    packageName: raw.pkg || '',
    storeName: raw.storeName || '',
    customName: raw.customStoreName || '',
    // 后端无 queryTimeMode：空数组即「不限」
    queryTimeMode: ranges.length ? 'ranges' : 'unlimited',
    timeRanges: ranges,
  }
}

const toRawRanges = (ranges: TimeRange[]) => ranges.map((item) => ({ start: item.startDate, end: item.endDate }))

export interface AccountListParams {
  keyword?: string
}

export interface UserSearchParams {
  keyword: string
  pageIndex: number
  pageSize: number
}

export interface ProductListParams {
  accountId: string
  keyword?: string
  pageIndex: number
  pageSize: number
}

export interface AddAccountBody {
  ssoUserId: string
  displayName: string
  userName: string
}

export interface CustomNameUpdateBody {
  accountId: string
  productIds: string[]
  customName: string
}

export interface QueryTimeUpdateBody {
  accountId: string
  productIds: string[]
  queryTimeMode: QueryTimeMode
  timeRanges: TimeRange[]
}

/** 查询有效企业账号列表（仅 archived=0） */
export const getEnterpriseAccountList = async (
  params: AccountListParams = {},
): Promise<LawyerResponse<LawyerAccount[]>> => {
  const res = await request(`/${serveName}/enterpriseAccount/list`, null, 'POST_JSON', {
    keyword: (params.keyword || '').trim(),
  })
  return wrap(res, (data) => (Array.isArray(data) ? data.map(toAccount) : []))
}

/** 新增账号弹窗的用户查询（透传用户中心 getSimpleUserInfoByPage，分页由前端控制） */
export const searchSimpleUsers = async (
  params: UserSearchParams,
): Promise<LawyerResponse<PageResult<SimpleUserInfo>>> => {
  const res = await request(`/${serveName}/enterpriseAccount/user/simplePage`, null, 'POST_JSON', {
    name: (params.keyword || '').trim(),
    currentPage: params.pageIndex,
    pageSize: params.pageSize,
  })
  return wrap(res, (data) => ({
    list: ((data?.list || []) as RawSimpleUser[]).map((item) => ({
      userId: String(item.id),
      userName: item.name || item.userName || String(item.id),
      loginName: item.userName || '',
    })),
    total: Number(data?.total || 0),
  }))
}

/**
 * 新增企业账号权限关系。
 * 若该 SSO 用户存在已归档记录，后端会恢复该行并保留历史产品配置（data.recovered=true）。
 */
export const addEnterpriseAccount = async (body: AddAccountBody): Promise<LawyerResponse<LawyerAccount>> => {
  const res = await request(`/${serveName}/enterpriseAccount/add`, null, 'POST_JSON', {
    ssoUserId: Number(body.ssoUserId),
    displayName: body.displayName,
    userName: body.userName,
  })
  return wrap(res, (data) =>
    data ? toAccount(data) : { accountId: '', ssoUserId: '', accountName: '', userName: '' },
  )
}

/** 归档企业账号：置 archived=1，产品配置保留，已归档幂等成功 */
export const deleteEnterpriseAccount = async (body: { accountId: string }): Promise<LawyerResponse<null>> => {
  const res = await request(`/${serveName}/enterpriseAccount/archive`, null, 'POST_JSON', {
    enterpriseAccountId: body.accountId,
  })
  return wrap(res, () => null)
}

/** 查询指定企业账号下的产品列表（后端按企账主键解析 SSO 产品权限并合并配置） */
export const getProductList = async (params: ProductListParams): Promise<LawyerResponse<PageResult<LawyerProduct>>> => {
  const res = await request(`/${serveName}/enterpriseAccount/app/list`, null, 'POST_JSON', {
    enterpriseAccountId: params.accountId,
    keyword: (params.keyword || '').trim(),
    pageNum: params.pageIndex,
    pageSize: params.pageSize,
  })
  return wrap(res, (data) => ({
    list: ((data?.list || []) as RawProduct[]).map(toProduct),
    total: Number(data?.total || 0),
  }))
}

/**
 * 产品配置唯一写接口。updateCustomStoreName / updateQueryTime 为 partial 开关，
 * 两者不得同时为 false；dbtIdList 长度为 1 即单条、大于 1 即批量。
 */
const saveProductConfig = async (body: {
  accountId: string
  productIds: string[]
  customName?: string
  queryTimeRanges?: Array<{ start: string; end: string }>
}): Promise<LawyerResponse<{ updatedCount: number }>> => {
  const updateCustomStoreName = body.customName !== undefined
  const updateQueryTime = body.queryTimeRanges !== undefined
  const res = await request(`/${serveName}/enterpriseAccount/app/saveConfig`, null, 'POST_JSON', {
    enterpriseAccountId: body.accountId,
    dbtIdList: body.productIds,
    updateCustomStoreName,
    ...(updateCustomStoreName ? { customStoreName: body.customName } : {}),
    updateQueryTime,
    ...(updateQueryTime ? { queryTimeRanges: body.queryTimeRanges } : {}),
  })
  return wrap(res, (data) => ({ updatedCount: Number(data?.updatedCount || 0) }))
}

/** 更新自定义商品名，单个与批量共用（productIds 传 1 个或 n 个；空串表示清空） */
export const updateProductCustomName = (body: CustomNameUpdateBody) =>
  saveProductConfig({
    accountId: body.accountId,
    productIds: body.productIds,
    customName: body.customName,
  })

/** 更新可查询时间，单个与批量共用（不限 = 下发空数组） */
export const updateProductQueryTime = (body: QueryTimeUpdateBody) =>
  saveProductConfig({
    accountId: body.accountId,
    productIds: body.productIds,
    queryTimeRanges: body.queryTimeMode === 'ranges' ? toRawRanges(body.timeRanges) : [],
  })

/** 当前登录用户是否为有效企业账号（收入看板分流用，本期页面未使用） */
export const getEnterpriseIdentity = async (): Promise<
  LawyerResponse<{ enterpriseAccount: boolean; enterpriseAccountId: string }>
> => {
  const res = await request(`/${serveName}/enterpriseAccount/identity`, null, 'POST_JSON', {})
  return wrap(res, (data) => ({
    enterpriseAccount: !!data?.enterpriseAccount,
    enterpriseAccountId: data?.enterpriseAccountId == null ? '' : String(data.enterpriseAccountId),
  }))
}
